/**
 * The shared WebAudio graph.
 *
 *   voices ┐                        ┌─> dry ────────────────┐
 *   beds   ┼─> (per-voice pan/send) ┤                       ├─> master ─> comp ─> out
 *   tension┘                        └─> reverbIn ─> conv ───┘
 *
 * One context, one master gain, one compressor, one convolver. The convolver's
 * impulse response is generated per ambience profile and swapped on a level
 * change; everything that wants to sound like it is in the room sends to it.
 */

import { TUNING } from '../core/config';
import { clamp01 } from '../core/math';
import { makeRNG } from '../core/rng';
import type { AmbienceProfile, RNG } from '../core/types';
import { makeBrownBuffer, makePinkBuffer, makeShaperCurve, makeWhiteBuffer } from './noise';
import { REVERBS, renderImpulse, type ReverbSpec } from './reverb';
import { makeListener, type Listener } from './spatial';
import { VoicePool } from './voices';

/** Fixed seed: generated buffers and impulses are identical in every session. */
const AUDIO_SEED = 0xa0d10;

export type NoiseKind = 'white' | 'pink' | 'brown';

export class AudioGraph {
  readonly ctx: AudioContext;
  readonly rng: RNG = makeRNG(AUDIO_SEED);
  readonly listener: Listener = makeListener();
  readonly pool: VoicePool;

  /** Master volume. Everything audible passes through here. */
  readonly master: GainNode;
  readonly compressor: DynamicsCompressorNode;
  /** Unreverbed sum. */
  readonly dry: GainNode;
  /** Send bus into the convolver. */
  readonly reverbIn: GainNode;
  readonly convolver: ConvolverNode;
  /** Convolver return level, set by the current space. */
  readonly reverbOut: GainNode;
  /** Ambience beds and the tension layer live here, upstream of `dry`. */
  readonly bed: GainNode;

  readonly white: AudioBuffer;
  readonly pink: AudioBuffer;
  readonly brown: AudioBuffer;
  /** Soft-clip curve for saturated voices. */
  readonly shaper: Float32Array<ArrayBuffer>;

  private readonly impulses = new Map<AmbienceProfile, AudioBuffer>();
  private pendingIR: AudioBuffer | null = null;
  private pendingIRAt = 0;
  private pendingWet = REVERBS.lobby.wet;
  private volume = TUNING.audio.masterVolume;
  private muted = false;

  constructor(ctx: AudioContext) {
    this.ctx = ctx;

    this.compressor = ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -13;
    this.compressor.knee.value = 22;
    this.compressor.ratio.value = 5;
    this.compressor.attack.value = 0.006;
    this.compressor.release.value = 0.28;
    this.compressor.connect(ctx.destination);

    this.master = ctx.createGain();
    this.master.gain.value = this.volume;
    this.master.connect(this.compressor);

    this.dry = ctx.createGain();
    this.dry.connect(this.master);

    this.convolver = ctx.createConvolver();
    // Left on deliberately: the spec's RMS calibration is what keeps a 0.55s
    // slap and a 4.8s hall at comparable perceived levels, so a space's `wet`
    // number means the same thing everywhere.
    this.convolver.normalize = true;
    this.reverbIn = ctx.createGain();
    this.reverbIn.connect(this.convolver);
    this.reverbOut = ctx.createGain();
    this.reverbOut.gain.value = REVERBS.lobby.wet;
    this.convolver.connect(this.reverbOut);
    this.reverbOut.connect(this.master);

    this.bed = ctx.createGain();
    this.bed.connect(this.dry);

    const gen = this.rng.fork(1);
    this.white = makeWhiteBuffer(ctx, gen);
    this.pink = makePinkBuffer(ctx, gen);
    this.brown = makeBrownBuffer(ctx, gen);
    this.shaper = makeShaperCurve(3.2);

    this.pool = new VoicePool(ctx, this.dry, this.reverbIn, this.listener);
    this.useReverb('lobby');
  }

  get now(): number {
    return this.ctx.currentTime;
  }

  buffer(kind: NoiseKind): AudioBuffer {
    return kind === 'pink' ? this.pink : kind === 'brown' ? this.brown : this.white;
  }

  /** Volume and mute both land on the master gain, with a click-free ramp. */
  setMaster(volume: number, muted: boolean): void {
    this.volume = clamp01(volume);
    this.muted = muted;
    const t = this.now;
    const g = this.master.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(muted ? 0 : this.volume, t + 0.02);
  }

  /**
   * Move to a different space. Swapping a live convolver's buffer clicks, so
   * the return is ducked first and the swap lands in `update`.
   */
  useReverb(profile: AmbienceProfile): void {
    const spec = REVERBS[profile] ?? REVERBS.lobby;
    const ir = this.impulseFor(profile, spec);
    if (!ir) return;
    if (this.convolver.buffer === ir) {
      this.rampWet(spec.wet, 0.5);
      return;
    }
    if (!this.convolver.buffer) {
      this.convolver.buffer = ir;
      this.rampWet(spec.wet, 0.2);
      return;
    }
    const t = this.now;
    const g = this.reverbOut.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(0, t + 0.3);
    this.pendingIR = ir;
    this.pendingIRAt = t + 0.32;
    this.pendingWet = spec.wet;
  }

  update(): void {
    if (this.pendingIR && this.now >= this.pendingIRAt) {
      this.convolver.buffer = this.pendingIR;
      this.pendingIR = null;
      this.rampWet(this.pendingWet, 0.8);
    }
    this.pool.update();
  }

  private rampWet(target: number, seconds: number): void {
    const t = this.now;
    const g = this.reverbOut.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(target, t + seconds);
  }

  /** Rendered on first use of a space, then cached for the session. */
  private impulseFor(profile: AmbienceProfile, spec: ReverbSpec): AudioBuffer | null {
    const cached = this.impulses.get(profile);
    if (cached) return cached;
    try {
      const ir = renderImpulse(this.ctx, spec, hashProfile(profile));
      this.impulses.set(profile, ir);
      return ir;
    } catch {
      return null;
    }
  }
}

function hashProfile(profile: AmbienceProfile): number {
  let h = 0;
  for (let i = 0; i < profile.length; i++) h = (Math.imul(h, 31) + profile.charCodeAt(i)) | 0;
  return h >>> 0;
}
